import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check, Lock, Play, ChevronLeft, ChevronRight, List, X, Scissors } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { VideoPlayer } from "@/components/site/VideoPlayer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ContactInstructor } from "@/components/site/ContactInstructor";

export default function Learn() {
  const { courseId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [currentId, setCurrentId] = useState(params.get("lesson") || null);
  const [playInfo, setPlayInfo] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const saveTimer = useRef(0);

  const { data: course, isLoading, isError, error } = useQuery({
    queryKey: ["learn", courseId],
    queryFn: async () => (await api.get(`/learning/course/${courseId}`)).data,
    retry: false,
  });

  const flatLessons = useMemo(() => {
    if (!course) return [];
    return course.sections.flatMap((s) => s.lessons);
  }, [course]);

  useEffect(() => {
    if (course && !currentId && flatLessons.length) setCurrentId(flatLessons[0].id);
  }, [course, flatLessons, currentId]);

  const current = flatLessons.find((l) => l.id === currentId);
  const currentIndex = flatLessons.findIndex((l) => l.id === currentId);

  useEffect(() => {
    if (!currentId) return;
    setPlayInfo(null);
    api.get(`/lessons/${currentId}/play`)
      .then(({ data }) => setPlayInfo(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Cannot load video"));
    track("lesson_view", { lesson_id: currentId });
  }, [currentId]);

  const saveProgress = (position, completed) => {
    api.post("/learning/progress", { lesson_id: currentId, last_position: position, completed })
      .then(() => { if (completed) qc.invalidateQueries({ queryKey: ["learn", courseId] }); })
      .catch(() => {});
  };

  const onProgress = (t) => {
    const now = Date.now();
    if (now - saveTimer.current > 8000) { saveTimer.current = now; saveProgress(t, undefined); }
  };

  const markComplete = () => { saveProgress(current?.last_position || 0, true); toast.success("Lesson completed"); };
  const goto = (i) => { if (flatLessons[i]) { setCurrentId(flatLessons[i].id); setSidebarOpen(false); } };

  if (isLoading) return <div className="flex min-h-screen items-center justify-center bg-black"><Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" /></div>;
  if (isError) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-black text-center px-6">
      <Lock className="h-12 w-12 text-[#DCA846]" />
      <p className="text-zinc-300">{error?.response?.data?.detail || "You don't have access to this course."}</p>
      <Link to="/store"><Button className="bg-[#DCA846] text-black">Browse Courses</Button></Link>
    </div>
  );

  const Sidebar = () => (
    <div className="flex h-full flex-col">
      <div className="border-b border-white/10 p-4">
        <Link to={`/course/${course.slug}`} className="font-display text-lg font-semibold uppercase leading-tight hover:text-[#DCA846]">{course.title}</Link>
        <p className="mt-1 text-xs text-zinc-500">{course.sections.reduce((a, s) => a + s.lessons.length, 0)} lessons</p>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar">
        {course.sections.map((sec, si) => (
          <div key={sec.id || si}>
            <div className="bg-zinc-950/60 px-4 py-2 text-xs font-display uppercase tracking-wider text-zinc-400">{sec.title}</div>
            {sec.lessons.map((l) => {
              const idx = flatLessons.findIndex((x) => x.id === l.id);
              return (
                <button key={l.id} data-testid={`sidebar-lesson-${l.id}`} onClick={() => goto(idx)}
                  className={`flex w-full items-center gap-3 border-l-2 px-4 py-3 text-left text-sm transition-colors ${
                    l.id === currentId ? "border-[#DCA846] bg-[#DCA846]/10 text-white" : "border-transparent text-zinc-300 hover:bg-white/5"
                  }`}>
                  {l.completed ? <Check className="h-4 w-4 shrink-0 text-emerald-400" /> : <Play className="h-4 w-4 shrink-0 text-zinc-500" />}
                  <span className="flex-1 line-clamp-2">{l.title}</span>
                  {l.duration && <span className="text-xs text-zinc-600">{l.duration}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#0A0A0A]">
      {/* Desktop sidebar */}
      <aside className="hidden w-80 shrink-0 border-r border-white/10 bg-zinc-900/40 lg:block">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 bg-zinc-950 border-r border-white/10">
            <button onClick={() => setSidebarOpen(false)} className="absolute right-3 top-3 text-white"><X className="h-5 w-5" /></button>
            <Sidebar />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2"><Scissors className="h-5 w-5 text-[#DCA846]" /></Link>
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden flex items-center gap-1 text-sm text-zinc-300"><List className="h-4 w-4" /> Lessons</button>
          </div>
          <Link to="/my-learning" className="text-sm text-zinc-400 hover:text-[#DCA846]">My Learning</Link>
        </div>

        <div className="mx-auto max-w-5xl p-4 sm:p-8">
          {playInfo ? (
            <VideoPlayer
              key={currentId}
              lessonId={currentId}
              source={playInfo}
              videoType={current?.video_type}
              startAt={current?.last_position || 0}
              onProgress={onProgress}
              onEnded={markComplete}
            />
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-lg border border-white/10 bg-zinc-900">
              <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />
            </div>
          )}

          <div className="mt-6">
            <h1 className="font-display text-2xl font-semibold text-white">{current?.title}</h1>
            {current?.description && <p className="mt-2 text-zinc-400 whitespace-pre-line">{current.description}</p>}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
            <Button data-testid="prev-lesson-btn" variant="outline" disabled={currentIndex <= 0} onClick={() => goto(currentIndex - 1)}
              className="border-white/15 text-white disabled:opacity-30">
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            {!current?.completed && (
              <Button data-testid="mark-complete-btn" onClick={markComplete} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                <Check className="mr-1 h-4 w-4" /> Mark Complete
              </Button>
            )}
            <Button data-testid="next-lesson-btn" disabled={currentIndex >= flatLessons.length - 1} onClick={() => goto(currentIndex + 1)}
              className="bg-[#DCA846] text-black hover:bg-[#C5963B] disabled:opacity-30">
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          <div className="mt-10">
            <ContactInstructor
              context={`${course.title}${current?.title ? " — " + current.title : ""}`}
              course={course.title}
              lesson={current?.title || ""}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
